import { browserTimeZone } from '@shared/model/preferences';

const FALLBACK_ZONES = [
  'UTC',
  'Africa/Cairo',
  'Africa/Casablanca',
  'America/New_York',
  'America/Toronto',
  'Asia/Baghdad',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kuala_Lumpur',
  'Asia/Jakarta',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Paris',
];

let cache: string[] | null = null;

/** All IANA zones known to the browser, plus UTC and the detected zone. */
export function listTimeZones(): string[] {
  if (cache) return cache;
  let zones: string[] = [];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = FALLBACK_ZONES;
  }
  const set = new Set(zones);
  set.add('UTC');
  set.add(browserTimeZone());
  cache = [...set].sort((a, b) => a.localeCompare(b));
  return cache;
}

export function isKnownTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Offset of `timeZone` from UTC at `instant`, in minutes (e.g. +180 for Asia/Riyadh). */
function offsetMinutes(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  return Math.round((asUtc - instant) / 60_000);
}

/**
 * A `<input type="datetime-local">` value ("2026-09-29T16:00") read as wall time in `timeZone`
 * (the viewer's profile zone, not the browser's), returned as an ISO instant. Null when empty/invalid.
 */
export function zonedInputToIso(value: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  let guess = wall - offsetMinutes(wall, timeZone) * 60_000;
  // Second pass settles DST transitions.
  guess = wall - offsetMinutes(guess, timeZone) * 60_000;
  return new Date(guess).toISOString();
}
